import { useCallback, useRef } from 'react';
import { fieldIsHeavy, selectionConsumesField } from '../utils/largeValueGesture';
import {
  MAX_VALUE_DOM_CHARS,
  RESTING_DOM_CHARS,
  nestedHtmlToValue,
  paintNestedValue,
} from '../utils/nestedHtml';
import { placeCaretBeforeTail } from '../utils/placeFieldCaret';

/** The stored value, including the tail that stays off the DOM until focus. */
export function useNestedStoredValue({ value, resolveVariable, elRef, readOnly, onCommit }) {
  const focusedRef = useRef(false);
  const lastCommittedRef = useRef(value || '');
  const tailRef = useRef('');
  const allSelectedRef = useRef(false);

  const paint = useCallback((raw) => {
    const max = focusedRef.current ? MAX_VALUE_DOM_CHARS : RESTING_DOM_CHARS;
    tailRef.current = paintNestedValue(elRef.current, raw, resolveVariable, max);
  }, [elRef, resolveVariable]);

  const storedValue = useCallback(() => {
    const el = elRef.current;
    if (!el) return '';
    const marker = el.querySelector('[data-large-tail]');
    return nestedHtmlToValue(el) + (marker ? tailRef.current : '');
  }, [elRef]);

  const readFull = useCallback(() => {
    const el = elRef.current;
    if (!el) return '';
    if (el.querySelector('[data-large-tail], [data-var-ref], [data-doc-math]')) {
      return storedValue();
    }
    return (el.textContent || '').replace(/\u200B/g, '');
  }, [elRef, storedValue]);

  const heavyField = () => fieldIsHeavy(elRef.current, tailRef.current.length);

  const ownsWholeValue = () =>
    allSelectedRef.current || (heavyField() && selectionConsumesField(elRef.current));

  const clearAllSelected = () => {
    allSelectedRef.current = false;
    elRef.current?.removeAttribute('data-value-all-selected');
  };

  const clearStored = () => {
    clearAllSelected();
    tailRef.current = '';
    const el = elRef.current;
    if (el) el.textContent = '';
  };

  const writeStored = (text) => {
    clearAllSelected();
    paint(text);
    placeCaretBeforeTail(elRef.current);
  };

  const selectEntireValue = () => {
    const el = elRef.current;
    if (!el) return;
    allSelectedRef.current = true;
    el.setAttribute('data-value-all-selected', '');
    const sel = window.getSelection();
    try {
      const range = document.createRange();
      range.setStart(el, 0);
      range.collapse(true);
      sel?.removeAllRanges();
      sel?.addRange(range);
    } catch {
      /* ignore caret failures */
    }
  };

  const commit = useCallback(() => {
    const el = elRef.current;
    if (!el || readOnly) return;
    if (el.querySelector('[data-var-insert-mark], [data-math-insert-mark]')) return;
    const next = storedValue();
    if (next === lastCommittedRef.current) return;
    lastCommittedRef.current = next;
    onCommit?.(next, { silent: false });
  }, [elRef, onCommit, readOnly, storedValue]);

  return {
    focusedRef,
    lastCommittedRef,
    allSelectedRef,
    paint,
    storedValue,
    readFull,
    heavyField,
    ownsWholeValue,
    clearAllSelected,
    clearStored,
    writeStored,
    selectEntireValue,
    commit,
  };
}
